import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppState } from '../index';
import { ParticipantService } from './participant.service';

import { Store } from '@ngrx/store';
import { WebSocketService, passWhenAlive } from '@qtek/core/websockets-core';
import { Message, ParticipantViews } from '@qtek/shared/models';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { selectMainCompany } from '../user';
import { ParticipantActions } from './participants.actions';

@Injectable()
export class ParticipantsEffects {
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private participantService: ParticipantService,
    private webSocketService: WebSocketService
  ) {}

  connectDealBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.connectDealBookList),
      withLatestFrom(this.store.select(selectMainCompany)),
      switchMap(([{ payload }, acn]) => {
        const subPayload =
          payload === 'investor'
            ? {
                prms: { leadAcnId: acn._id, p: 'lead' },
                mysid: ParticipantViews.SALES_PIPELINE,
                view: ParticipantViews.INVESTORS,
                s: 'model',
              }
            : {
                prms: { acnId: acn._id },
                mysid: ParticipantViews.DEAL_BOOK,
                view: ParticipantViews.DEAL_BOOK,
                s: 'model',
              };
        return this.participantService
          .subscribe(subPayload)
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(ParticipantActions.disconnectDealBookList)
              )
            )
          );
      }),
      filter(({ op }) => Boolean(op)),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return res
              ? ParticipantActions.getDealBookSuccess({
                  payload: res,
                  meta: sts?.meta,
                })
              : ParticipantActions.getDealBookFailure({ error: sts });
          case 'ins':
            return res
              ? ParticipantActions.createDealBookSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_CREATED'),
                })
              : ParticipantActions.createDealBookFailure({ error: sts });
          case 'upd':
            return res
              ? ParticipantActions.updateDealBookSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_UPDATED'),
                })
              : ParticipantActions.updateDealBookFailure({ error: sts });
          case 'del':
            return res
              ? ParticipantActions.deleteDealBookSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_DELETED'),
                })
              : ParticipantActions.deleteDealBookFailure({ error: sts });
          default:
            return { type: 'NOT_EXIST' };
        }
      })
    )
  );

  subscribeDealBookWS$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.subscribeDealBookList),
      switchMap(({ type, ...payload }) => {
        const subPayload = {
          q: payload.query as any,
          view: ParticipantViews.DEAL_BOOK,
          mysid: `${ParticipantViews.DEAL_BOOK}_${payload.viewId}`,
        };
        return this.participantService.subscribe(subPayload).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(ParticipantActions.unsubscribeDealBookList),
              filter(action => action.viewId === payload.viewId)
            )
          )
        );
      }),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return res
              ? ParticipantActions.getDealBookListSuccess({
                  payload: res,
                  meta: sts?.meta,
                })
              : ParticipantActions.getDealBookListFailure({ error: sts });
          case 'ins':
            return res
              ? ParticipantActions.createDealBookSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_CREATED'),
                })
              : ParticipantActions.createDealBookFailure({
                  error: sts,
                });
          case 'upd':
            return res
              ? ParticipantActions.updateDealBookSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_UPDATED'),
                })
              : ParticipantActions.updateDealBookFailure({
                  error: sts,
                });
          case 'del':
            return res
              ? ParticipantActions.deleteDealBookSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_DELETED'),
                })
              : ParticipantActions.deleteDealBookFailure({
                  error: sts,
                });
          default:
            return { type: 'NOT_EXIST' };
        }
      })
    )
  );

  getDealBooksWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.getDealBookList),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.getDealBookWS(payload)
        )
      ),
    { dispatch: false }
  );

  createDealBookWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.createDealBook),
        tap(({ payload }) => {
          this.participantService
            .addDealBook(payload)
            .pipe(take(1))
            .subscribe();
        })
      ),
    { dispatch: false }
  );

  updateDealBookWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.updateDealBook),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.updateDealBookWS(payload)
        )
      ),
    { dispatch: false }
  );

  deleteDealBookWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.deleteDealBook),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.deleteDealBookWS(payload)
        )
      ),
    { dispatch: false }
  );

  resentInvestorInvite = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.resentInvestorInvite),
      passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
      mergeMap(({ type, id }) =>
        this.participantService.resentInvitation(id).pipe(
          map(() =>
            ParticipantActions.resentInvestorInviteSuccess({
              message: new Message(''),
            })
          ),
          catchError(({ error }) =>
            of(ParticipantActions.resentInvestorInviteFailure({ error }))
          )
        )
      )
    )
  );

  getInvests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.getInvests),
      mergeMap(() =>
        this.participantService.getInvest().pipe(
          map(res => ParticipantActions.getInvestsSuccess({ payload: res })),
          catchError(({ error }) =>
            of(ParticipantActions.getInvestsFailure({ error }))
          )
        )
      )
    )
  );

  requestInvests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.requestInvests),
      mergeMap(({ payload }) =>
        this.participantService.requestInvest(payload).pipe(
          map(res =>
            ParticipantActions.requestInvestsSuccess({
              payload: res,
              message: new Message('GNR_INVESTS_REQUEST'),
            })
          ),
          catchError(({ error }) =>
            of(ParticipantActions.requestInvestsFailure({ error }))
          )
        )
      )
    )
  );

  subscribeInvestorsWS$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.subscribeInvestorList),
      switchMap(({ type, ...payload }) =>
        this.participantService
          .subscribe({
            q: payload.query as any,
            view: ParticipantViews.INVESTORS,
            mysid: `${ParticipantViews.INVESTORS}_${payload.viewId}`,
            cmd: payload.withoutQuery === true ? undefined : 'query',
          })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(ParticipantActions.unsubscribeInvestorList),
                filter(action => action.viewId === payload.viewId)
              )
            )
          )
      ),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return res
              ? ParticipantActions.getInvestorListSuccess({ payload: res })
              : ParticipantActions.getInvestorListFailure({ error: sts });
          case 'ins':
            return res
              ? ParticipantActions.addInvestorSuccess({ payload: res })
              : ParticipantActions.addInvestorFailure({ error: sts });
          case 'upd':
            return res
              ? ParticipantActions.updateInvestorSuccess({
                  payload: res,
                })
              : ParticipantActions.updateInvestorFailure({
                  error: sts,
                });
          case 'del':
            return res
              ? ParticipantActions.deleteInvestorSuccess({
                  payload: res,
                  message: new Message('GNR_DEAL_BOOK_DELETED'),
                })
              : ParticipantActions.deleteInvestorFailure({
                  error: sts,
                });
          default:
            return { type: 'NOT_EXIST' };
        }
      })
    )
  );

  getInvestorsWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.getInvestorList),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.getInvestorsWS(payload)
        )
      ),
    { dispatch: false }
  );

  addInvestorWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.addInvestor),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.addInvestorsWS(payload)
        )
      ),
    { dispatch: false }
  );

  updateInvestorWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.updateInvestor),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.updateInvestorWS(payload)
        )
      ),
    { dispatch: false }
  );

  deleteInvestorWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.deleteInvestor),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.deleteInvestorWS(payload)
        )
      ),
    { dispatch: false }
  );

  subscribeSalesPipelineWS$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParticipantActions.subscribeSalesPipelineList),
      switchMap(({ type, ...payload }) =>
        this.participantService
          .subscribe({
            q: payload.query as any,
            s: 'model',
            view: ParticipantViews.INVESTORS,
            mysid: `${ParticipantViews.SALES_PIPELINE}_${payload.viewId}`,
          })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(ParticipantActions.unsubscribeSalesPipelineList),
                filter(({ viewId }) => viewId === payload.viewId)
              )
            )
          )
      ),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return res
              ? ParticipantActions.getSalesPipelineListSuccess({
                  res,
                  meta: sts?.meta,
                })
              : ParticipantActions.getSalesPipelineListFailure({
                  error: sts,
                });
          case 'ins':
            return res
              ? ParticipantActions.createSalesPipelineSuccess({
                  payload: res,
                })
              : ParticipantActions.createSalesPipelineFailure({
                  error: sts,
                });
          case 'upd':
            return res
              ? ParticipantActions.updateSalesPipelineSuccess({
                  payload: res,
                })
              : ParticipantActions.updateSalesPipelineFailure({
                  error: sts,
                });
          case 'del':
            return res
              ? ParticipantActions.deleteSalesPipelineSuccess({
                  payload: res,
                })
              : ParticipantActions.deleteSalesPipelineFailure({
                  error: sts,
                });
          default:
            return { type: 'NOT_EXIST' };
        }
      })
    )
  );

  getSalesPipelineWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.getSalesPipelineList),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.getSalesPipelineWS(payload)
        )
      ),
    { dispatch: false }
  );

  createSalesPipelineWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.createSalesPipeline),
        tap(({ payload }) => {
          // this.participantService.addDealBook(payload).pipe(take(1)).subscribe();
        })
      ),
    { dispatch: false }
  );

  updateSalesPipelineWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.updateSalesPipeline),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.updateSalesPipelineWS(payload)
        )
      ),
    { dispatch: false }
  );

  deleteSalesPipelineWS$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ParticipantActions.deleteSalesPipeline),
        passWhenAlive(this.webSocketService.getWebSocketIsAlive$()),
        tap(({ type, ...payload }) =>
          this.participantService.deleteSalesPipelineWS(payload)
        )
      ),
    { dispatch: false }
  );
}
